import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { getClosest, isScrolled } from '../../utils/ids-dom-utils/ids-dom-utils';
import { stripHTML } from '../../utils/ids-xss-utils/ids-xss-utils';
import { contrastColor, adjustColor, hexToRgba } from '../../utils/ids-color-utils/ids-color-utils';

import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsOrientationMixin from '../../mixins/ids-orientation-mixin/ids-orientation-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsElement from '../../core/ids-element';
import IdsGlobal from '../ids-global/ids-global';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsHeader from '../ids-header/ids-header';

import './ids-tab-more';
import './ids-tab';
import './ids-tab-divider';
import './ids-tab-content';
import './ids-tabs-context';

import type IdsTab from './ids-tab';
import type IdsTabMore from './ids-tab-more';

import styles from './ids-tabs.scss';
import IdsLocale from '../ids-locale/ids-locale';
import IdsPopupMenu from '../ids-popup-menu/ids-popup-menu';

export interface LocaleMixinInterface {
  localeAPI: IdsLocale;
}

const Base = IdsOrientationMixin(
  IdsLocaleMixin(
    IdsColorVariantMixin(
      IdsKeyboardMixin(
        IdsEventsMixin(
          IdsElement
        )
      )
    )
  )
);

/**
 * IDS Tabs Component
 * @type {IdsTabs}
 * @inherits IdsElement
 * @mixes IdsColorVariantMixin
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsOrientationMixin
 */
@customElement('ids-tabs')
@scss(styles)
export default class IdsTabs extends Base {
  #isInitializing = true;

  constructor() {
    super();
  }

  get localeAPI() {
    return IdsGlobal.getLocale();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#isInitializing = true;
    this.setAttribute(htmlAttributes.ROLE, 'tablist');
    this.#connectMoreTabs();
    this.#detectParentColorVariant();
    this.#attachEventHandlers();
    this.#ro.observe(this.container as any);

    const selected: any = this.querySelector('[selected]') || this.querySelector('[value]');
    // Defer the initial tab selection until after the initial setup phase
    requestAnimationFrame(() => {
      const currentlySelectedTab = this.querySelector('[selected]');
      const toSelect = currentlySelectedTab ?? selected;

      this.#selectTab(toSelect);
      this.#selectFirstSubTab(toSelect);
      this.#isInitializing = false;
    });

    this.#attachAfterRenderEvents();
    this.#configurePopupPlacement();

    if (this.hasAppMenu) {
      this.container?.classList.add('has-app-menu');
    }

    if (this.hideHeaders) {
      this.container?.classList.add('hide-headers');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#ro.disconnect();
  }

  onLocaleChange = (locale?: IdsLocale): void => {
    if (locale?.isRTL()) this.container?.classList.add('rtl');
    else this.container?.classList.remove('rtl');
  };

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<any> {
    return [
      ...super.attributes,
      attributes.COLOR,
      attributes.HIDE_HEADERS,
      attributes.VALUE
    ];
  }

  /**
   * @returns {string} template for Tab List
   */
  template() {
    return `<div class="ids-tabs-container">
      <div class="ids-tabs-list">
        <slot></slot>
      </div>
      <div class="ids-tabs-list-more">
        <slot name="fixed"></slot>
      </div>
    </div>`;
  }

  /**
   * Watches for changes to the Tab List size and recalculates overflowed tabs, if applicable
   * @private
   * @property {ResizeObserver} ro this Popup component's resize observer
   */
  #ro = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target.classList.contains('ids-tabs-container')) {
        this.resize();
      }
    }
  });

  /**
   * Runs whenever the Tab List's size changes
   */
  resize(): void {
    this.refreshOverflowedTabs();
  }

  /**
   * Inherited from `IdsColorVariantMixin`
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = ['alternate', 'module', 'header'];

  /**
   * @property {string} value stores a tab's value (used for syncing tab state with displayed content)
   */
  #value = '';

  /**
   * @param {string} value A value which represents a currently selected tab
   */
  set value(value) {
    const currentValue = this.#value;
    const isValidValue = this.hasTab(value);

    if (isValidValue && currentValue !== value) {
      this.#value = value;
      this.setAttribute(attributes.VALUE, value);
      this.#selectTab(this.querySelector(`ids-tab[value="${value}"]`));
      if (!this.#isInitializing) {
        this.triggerEvent('change', this, {
          bubbles: false,
          detail: { elem: this, value }
        });
      }
    } else {
      this.setAttribute(attributes.VALUE, this.value);
    }
  }

  /**
   * @returns {string} The value representing a currently selected tab
   */
  get value() {
    return this.#value;
  }

  /**
   * Sets the color attribute
   * @param {string} value string value for color
   */
  set color(value: string) {
    if (this.colorVariant !== 'module') return;
    if (typeof value !== 'string' || !value.length || value === '' || value === '#ffffff') {
      this.removeAttribute('color');
      this.container?.style.removeProperty('--ids-tab-module-color-background-selected');
      this.container?.style.removeProperty('--ids-tab-module-color-text-selected');
      this.container?.style.removeProperty('--ids-button-module-color-background-hover');
      this.container?.style.removeProperty('--ids-button-module-color-text-hover');
      this.container?.style.removeProperty('--ids-tab-module-color-border-right');
      this.container?.style.removeProperty('--ids-tab-module-color-border-bottom');
      this.container?.style.removeProperty('--ids-tab-module-color-background-default');
      this.container?.style.removeProperty('--ids-tab-module-color-text-default');
      this.container?.style.removeProperty('--ids-tab-color-background-hover');
      this.container?.style.removeProperty('--ids-tab-color-text-hover');
      this.container?.style.removeProperty('--ids-tab-module-color-background-disabled');
      this.container?.style.removeProperty('--ids-tab-module-color-text-disabled');
      this.container?.style.removeProperty('--ids-tab-module-tab-color-border-bottom');
      this.container?.style.removeProperty('--ids-tab-overflow-effect-left');
      this.container?.style.removeProperty('--ids-tab-overflow-effect-right');
      this.container?.style.removeProperty('--ids-tab-module-color-background-hover');
      this.container?.style.removeProperty('--ids-tab-module-color-text-hover');

      return;
    }
    const sanitzedVal = stripHTML(value);
    const textColor = contrastColor(sanitzedVal, '#ffffff', '#000000');
    const strongestColor = textColor === '#ffffff' ? 'var(--ids-color-accent-neutral-strongest)' : 'var(--ids-color-accent-neutral-weakest)';

    this.container?.style.setProperty('--ids-tab-module-color-background-selected', sanitzedVal);
    this.container?.style.setProperty('--ids-tab-module-color-text-selected', textColor);
    this.container?.style.setProperty('--ids-button-module-color-background-hover', 'transparent');
    this.container?.style.setProperty('--ids-button-module-color-text-hover', textColor);
    this.container?.style.setProperty('--ids-tab-module-color-border-right', strongestColor);
    this.container?.style.setProperty('--ids-tab-module-color-border-bottom', strongestColor);
    this.container?.style.setProperty('--ids-tab-module-color-background-default', adjustColor(sanitzedVal, -0.25));
    this.container?.style.setProperty('--ids-tab-module-color-text-default', textColor);
    this.container?.style.setProperty('--ids-tab-color-background-hover', adjustColor(sanitzedVal, -0.25));
    this.container?.style.setProperty('--ids-tab-color-text-hover', textColor);
    this.container?.style.setProperty('--ids-tab-module-color-background-disabled', hexToRgba(adjustColor(sanitzedVal, -0.25), 0.75));
    this.container?.style.setProperty('--ids-tab-module-color-text-disabled', hexToRgba(textColor, 0.75));
    this.container?.style.setProperty('--ids-tab-module-tab-color-border-bottom', strongestColor);
    this.container?.style.setProperty('--ids-tab-overflow-effect-left', `linear-gradient(to right, ${adjustColor(sanitzedVal, -0.25)}, rgba(0 0 0 / 0))`);
    this.container?.style.setProperty('--ids-tab-overflow-effect-right', `linear-gradient(to right, rgba(0 0 0 / 0), ${adjustColor(sanitzedVal, -0.25)})`);
    this.container?.style.setProperty('--ids-tab-module-color-background-hover', adjustColor(sanitzedVal, -0.4));
    this.container?.style.setProperty('--ids-tab-module-color-text-hover', textColor);

    this.setAttribute(attributes.COLOR, sanitzedVal);
  }

  get color(): string {
    return this.getAttribute(attributes.COLOR) || '#ffffff';
  }

  /**
   * Sets whether to hide tab headers
   * @param {boolean | string} value True to hide headers
   */
  set hideHeaders(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.HIDE_HEADERS, '');
      this.container?.classList.add('hide-headers');
    } else {
      this.removeAttribute(attributes.HIDE_HEADERS);
      this.container?.classList.remove('hide-headers');
    }
  }

  /**
   * @returns {boolean} True if headers are hidden
   */
  get hideHeaders(): boolean {
    return this.hasAttribute(attributes.HIDE_HEADERS);
  }

  /**
   * @returns {HTMLElement} Tab List container element reference from the shadow root
   */
  get tabListContainer() {
    return this.container?.querySelector<HTMLElement>('.ids-tabs-list');
  }

  /**
   * @returns {HTMLElement} More Container
   */
  get moreContainer() {
    return this.container?.querySelector<HTMLElement>('.ids-tabs-list-more');
  }

  /**
   * @returns {Array<HTMLElement>} tabs that are connected to this component's Main slot
   */
  get tabListElements() {
    const mainSlot = this.container?.querySelector<HTMLSlotElement>('slot:not([name])');
    let tabElements: Array<HTMLElement> = [];
    const firstAssignedElem = mainSlot?.assignedElements()[0];

    if (firstAssignedElem?.tagName === 'IDS-SWAPPABLE') {
      for (let i = 0; i < firstAssignedElem?.children.length; i++) {
        const swapItem = firstAssignedElem?.children[i];
        tabElements.push(swapItem.firstElementChild as HTMLElement);
      }
    } else {
      tabElements = mainSlot?.assignedElements() as Array<HTMLElement>;
    }
    return tabElements;
  }

  get lastTab(): IdsTab | undefined {
    return [...this.querySelectorAll<IdsTab>('ids-tab')].pop();
  }

  get tabs(): IdsTab[] {
    return [...this.querySelectorAll<IdsTab>('ids-tab')];
  }

  get currentlyEditingTab(): IdsTab[] {
    return [...this.querySelectorAll<IdsTab>('ids-tab[editing]')];
  }

  /**
   * @readonly
   * @returns {any} [IdsTab | null] The last possible tab with a usable value in the list
   */
  get lastNavigableTab(): any {
    return [...this.querySelectorAll('ids-tab[value]:not([actionable]):not([aria-disabled="true"]):not([overflowed])')].pop();
  }

  /**
   * Reference to the currently-selected tab, if applicable
   * @param {string} value the tab value to scan
   * @returns {boolean} true if this tab list contains a tab with the provided value
   */
  hasTab(value: string): boolean {
    return this.querySelector(`ids-tab[value="${value}"]`) !== null;
  }

  /**
   * Indicates whether the tab list contains an app-menu tab
   * @returns {boolean} true if the app menu is present
   */
  get hasAppMenu(): boolean {
    return this.querySelector('[value="app-menu"]') !== null;
  }

  /**
   * Check if a tab is disable by value
   * @param {string} value the tab value to scan
   * @returns {boolean} true if this tab is disabled
   */
  isTabDisabled(value: string): boolean {
    return this.querySelector(`ids-tab[value="${value}"]`)?.getAttribute('aria-disabled') === 'true' || false;
  }

  /**
   * Select the first tab on render
   * @param {any} tab the new tab to select
   * @returns {void}
   */
  #selectFirstSubTab(tab: any) {
    const tabEl = tab?.shadowRoot?.querySelector('.ids-tab');
    if (!tabEl) return;

    const hasSubTabs = tabEl.classList.contains('has-sub-tabs');
    if (!hasSubTabs) return;

    const subTab = tab?.shadowRoot?.querySelector('ids-sub-tab');
    if (subTab) {
      this.triggerEvent('tabselect', this, {
        bubbles: true,
        detail: {
          selected: true,
          value: subTab.value
        }
      });
    }
  }

  /**
   * Traverses parent nodes and scans for parent IdsHeader components.
   * If an IdsHeader is found, adjusts this component's ColorVariant accordingly.
   * @returns {void}
   */
  #detectParentColorVariant() {
    let isHeaderDescendent = false;
    let currentElement = (this as any).host || this.parentNode;

    while (!isHeaderDescendent && currentElement) {
      if (currentElement instanceof IdsHeader) {
        isHeaderDescendent = true;
        break;
      }

      // consider the body the ceiling of where to reach here
      if (currentElement.tagName === 'BODY') {
        break;
      }

      currentElement = currentElement.host || currentElement.parentNode;
    }
  }

  /**
   * When a child value or this component value changes,
   * called to rebind onclick callbacks to each child
   * @returns {void}
   */
  #attachEventHandlers() {
    // Reusable handlers
    const nextTabHandler = (e: Event) => {
      if (this.currentlyEditingTab.length > 0) return;

      const nextTab = this.nextTab((e.target as any).closest('ids-tab, ids-tab-more'));
      nextTab.focus();
      requestAnimationFrame(() => {
        nextTab.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      });
    };
    const prevTabHandler = (e: Event) => {
      if (this.currentlyEditingTab.length > 0) return;

      const prevTab = this.prevTab((e.target as any).closest('ids-tab, ids-tab-more'));
      prevTab.focus();
      requestAnimationFrame(() => {
        if (this.currentlyEditingTab.length > 0) return;

        prevTab.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      });
    };

    // Add key listeners and consider orientation for assignments
    this.listen('ArrowLeft', this, prevTabHandler);
    this.listen('ArrowRight', this, nextTabHandler);
    this.listen('ArrowUp', this, prevTabHandler);
    this.listen('ArrowDown', this, nextTabHandler);

    // Home/End keys should navigate to beginning/end of Tab list respectively
    this.listen('Home', this, () => {
      if (this.currentlyEditingTab.length > 0) return;

      (this.children[0] as HTMLElement)?.focus();
    });
    this.listen('End', this, () => {
      if (this.currentlyEditingTab.length > 0) return;

      (this.children[this.children.length - 1] as HTMLElement)?.focus();
    });

    // Add Events/Key listeners for Tab Selection via click/keyboard
    this.listen(['Enter', ' '], this, (e: KeyboardEvent) => {
      const elem: any = e.target;
      if (elem) {
        if (elem.tagName === 'IDS-TAB') {
          if (this.currentlyEditingTab.length > 0) {
            if (e.code === 'Space') {
              return;
            }

            this.#tabButtonInteraction(elem, 'update');
          }

          this.#selectTab(elem);
        }
        if (elem.tagName === 'IDS-TAB-MORE') {
          if (!elem.menu.visible) {
            elem.menu.showIfAble();
          } else {
            elem.menu.hide();
            elem.focus();
          }
        }
      }
    });

    // Listen for Delete (Mac) or Backspace (PC) for removal events
    const dismissOnKeystroke = (e: CustomEvent) => {
      const elem: any = e.target;
      if (elem) {
        if (elem.tagName === 'IDS-TAB') {
          if ((elem as IdsTab).editing) return;
          this.#tabButtonInteraction(elem, 'close');
        }
      }
    };
    this.listen('Delete', this, dismissOnKeystroke);
    this.listen('Backspace', this, dismissOnKeystroke);

    this.onEvent('tabselect', this, (e: CustomEvent) => {
      const elem: any = e.target;
      if (elem && elem.tagName === 'IDS-TAB') {
        this.#selectTab(elem);
      }
    });

    this.onEvent('click.tabs', this, (e: PointerEvent) => {
      const elem: any = e.target;
      if (elem) {
        if (elem.tagName === 'IDS-TAB') {
          if (elem.hasAttribute('sub-tabs')) {
            const tabsMore = this.querySelector('ids-tab-more');
            let tabsMoreWidth = 0;
            if (tabsMore) {
              tabsMoreWidth = tabsMore.getBoundingClientRect().width + 4;
            }

            const tabsArea = this.getBoundingClientRect();
            const elemArea = elem.getBoundingClientRect();
            const tabsRight = tabsArea.width - tabsMoreWidth;

            if ((elemArea.left + elemArea.width) > tabsRight || elemArea.left < tabsArea.left) {
              elem.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
              setTimeout(() => { this.#openSubTabMenu(elem); }, 500);
            } else {
              this.#openSubTabMenu(elem);
            }
          } else if ((!elem.disabled)) {
            this.#selectTab(elem);
          }
        }

        if (elem.tagName === 'IDS-TRIGGER-BUTTON' || getClosest(elem, 'ids-trigger-button')) {
          e.stopPropagation();
          const tab = getClosest(elem, 'ids-tab');
          const triggerButton = getClosest(elem, 'ids-trigger-button');
          this.#tabButtonInteraction(tab, triggerButton?.slot || elem.slot);
        }
      }
    });

    // Removes the tab from the list on `tabremove` events
    this.onEvent('tabremove', this, (e: CustomEvent) => {
      e.detail.elem.remove();
    });

    // Focusing via keyboard on an IdsTab doesn't automatically fire its `focus()` method.
    // This listener applies to all tabs in the list
    this.onEvent('focusin.tabs', this, (e: FocusEvent) => {
      const elem: any = e.target;
      if (elem && elem.tagName === 'IDS-TAB' && !(elem as IdsTab).editing) {
        elem.focus();
      }
    });
  }

  /**
   * Attaches event handlers that should be applied after rendering occurs
   */
  #attachAfterRenderEvents(): void {
    // Refreshes the tab list on change
    this.onEvent('slotchange', this.container, () => {
      this.#connectMoreTabs();
      this.refreshOverflowedTabs();
      this.#correctSelectedTab();
    });

    this.onEvent('scroll', this.shadowRoot?.querySelector('.ids-tabs-list') as HTMLElement, () => {
      this.refreshScrollEffect();
    });

    this.onEvent('resize', window, () => {
      this.refreshScrollEffect();
    });
  }

  #openSubTabMenu(target: any) {
    this.triggerEvent('click', target.shadowRoot?.querySelector('ids-menu-button'), {
      bubbles: true,
      detail: {
        selected: true,
        elem: this,
        button: 0,
        origin: 'ids-tabs'
      }
    });
  }

  /** Refresh the scroll effect on left and right side */
  refreshScrollEffect() {
    const scrollEl = this?.shadowRoot?.querySelector('.ids-tabs-list');
    scrollEl?.parentElement?.classList.remove('is-scrolled-left');
    scrollEl?.parentElement?.classList.remove('is-scrolled-right');
    const scrollResult = isScrolled(scrollEl as HTMLElement);

    if (scrollResult.isScrolledLeft) {
      scrollEl?.parentElement?.classList.add('is-scrolled-left');
    }
    if (scrollResult.isScrolledRight) {
      scrollEl?.parentElement?.classList.add('is-scrolled-right');
    }
  }

  /**
   * Configures any slotted `ids-tab-more` components present
   */
  #connectMoreTabs() {
    this.querySelector('ids-tab-more')?.setAttribute(attributes.SLOT, 'fixed');
  }

  /**
   * Navigates from a specified Tab to the next-available Tab in the list
   * @param {HTMLElement} currentTab an contained element (usually an IdsTab) to check for siblings
   * @returns {HTMLElement} the next tab in this Tab list's order
   */
  nextTab(currentTab: HTMLElement): HTMLElement {
    let nextTab: any = currentTab.nextElementSibling;

    // If next sibling isn't a tab or is disabled, try this method again on the found sibling
    if (nextTab && (!nextTab.tagName.includes('IDS-TAB') || nextTab.tagName.includes('IDS-TAB-DIVIDER') || nextTab.disabled)) {
      return this.nextTab(nextTab);
    }

    // If null, reset back to the first tab (cycling behavior)
    if (!nextTab) {
      nextTab = this.children[0];
    }

    return nextTab;
  }

  /**
   * Navigates from a specified Tab to the previously-available Tab in the list
   * @param {HTMLElement} currentTab an contained element (usually an IdsTab) to check for siblings
   * @returns {HTMLElement} the previous tab in this Tab list's order
   */
  prevTab(currentTab: HTMLElement): HTMLElement {
    let prevTab: any = currentTab.previousElementSibling;

    // If previous sibling isn't a tab or is disabled, try this method again on the found sibling
    if (prevTab && (!prevTab.tagName.includes('IDS-TAB') || prevTab.tagName.includes('IDS-TAB-DIVIDER') || prevTab.disabled)) {
      return this.prevTab(prevTab);
    }

    // If null, reset back to the last tab (cycling behavior)
    if (!prevTab) {
      prevTab = this.children[this.children.length - 1];
    }

    return prevTab;
  }

  /**
   * Selects a tab and syncs the entire tab list with the new selection
   * @param {any} tab the new tab to select
   * @returns {void}
   */
  #selectTab(tab: any): void {
    if (!tab) return;

    if (tab.actionable) {
      if (typeof tab.onAction === 'function') {
        tab.onAction(tab.selected);
      }
      return;
    }

    if (!tab.selected) {
      const current = this.querySelector<IdsTab>('[selected]');
      if (!current || (current && tab !== current)) {
        tab.selected = true;
        this.value = tab.value;
        if (current) {
          current.selected = false;
        }
      }
    } else if (tab.selected) {
      this.value = tab.value;

      const firstTab = this.querySelector<IdsTab>('ids-tab:first-child');
      const isFirstTab = tab === firstTab;

      const endOrStart = isFirstTab ? 'end' : 'center';
      const isOverFlow = this.container?.classList.contains('has-more-actions');
      if (isOverFlow) {
        tab.scrollIntoView({
          block: endOrStart,
          inline: endOrStart
        });
      }
    }
  }

  /**
   * Handles interactions with tab action buttons (close, update, cancel).
   * Delegates to the tab's button click handler and corrects tab selection if needed.
   * @param {any} tab - The tab containing the clicked button
   * @param {string} btnName - The name/purpose of the button clicked (close, update, cancel)
   * @returns {void}
   */
  #tabButtonInteraction(tab: any, btnName: string): void {
    if (!tab) return;

    tab.onTabButtonClick(btnName);
    this.#correctSelectedTab();
  }

  /**
   * Detects if a Tab no longer exists and selects an available one
   */
  #correctSelectedTab(): void {
    if (!this.#isInitializing && (!this.hasTab(this.value) || this.isTabDisabled(this.value))) {
      this.#selectTab(this.lastNavigableTab || this.lastTab);
    }
  }

  /**
   * Configure placement of popup-menu
   */
  #configurePopupPlacement(): void {
    let childList: any[] = [];
    if (this.firstElementChild?.tagName === 'IDS-TAB') {
      childList = Array.from(this.children);
    } else if (this.firstElementChild?.tagName === 'IDS-SWAPPABLE') {
      childList = Array.from(this.firstElementChild.children);
    } else {
      return;
    }

    // const childList = Array.from(this.children);
    childList.forEach((value: any) => {
      const val: IdsTab = value.tagName === 'IDS-SWAPPABLE-ITEM' ? value.firstElementChild : value;
      const popupMenu = val.shadowRoot?.querySelector<IdsPopupMenu>('ids-popup-menu')?.popup;
      const isOverFlow = popupMenu?.querySelector('#tab-more-menu');
      if (popupMenu && !isOverFlow) {
        popupMenu.onPlace = (popupRect: DOMRect) => {
          if (val.tagName === 'IDS-TAB-MORE') return popupRect;

          const param = val.getBoundingClientRect();
          popupRect.y -= 2;

          if (this.localeAPI?.isRTL()) popupRect.x -= 4;
          else popupRect.x = param.left;

          popupMenu.style.width = `${param.width}px`;
          return popupRect;
        };
      }
    });
  }

  /**
   * Attempts to refresh state of the Tab List related to overflowed tabs, if applicable
   */
  refreshOverflowedTabs(): void {
    const moreTab = this.querySelector<IdsTabMore>('ids-tab-more');
    const allMoreTabs = this.container?.querySelector<HTMLSlotElement>('.ids-tabs-list-more slot')?.assignedElements();

    if (moreTab && moreTab?.renderOverflowedItems) {
      moreTab.renderOverflowedItems();
      moreTab.refreshOverflowedItems();
      this.container?.classList[!moreTab.hidden ? 'add' : 'remove']('has-more-actions');

      let space = 45;
      if (allMoreTabs && allMoreTabs.length > 1) {
        space = 45 * allMoreTabs.length;
      }
      this.container?.style.setProperty('--more-tab-space-right', `${space - 3}px`);
      this.container?.style.setProperty('--more-tab-space-rtl-left', `${space - 3}px`);
    }
  }

  /**
   * Listen for changes to color variant, which updates each child tab.
   * @returns {void}
   */
  onColorVariantRefresh(): void {
    const tabs = [...this.querySelectorAll<IdsTab | IdsTabMore>('ids-tab, ids-tab-more')];
    tabs.forEach((tab) => {
      tab.colorVariant = this.colorVariant;
    });
  }

  /**
   * Listen for changes to orientation, which updates each child tab.
   * @returns {void}
   */
  onOrientationRefresh(): void {
    const tabs = [...this.querySelectorAll<IdsTab | IdsTabMore>('ids-tab, ids-tab-more')];
    tabs.forEach((tab) => {
      tab.orientation = this.orientation;
    });
    this.resize();
  }
}
